import { ui } from "./styles.js";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { createRootRouteWithContext, createRoute, createRouter } from "@tanstack/react-router";
import { BrandMark, LoadingState, StateNotice } from "@snowiespace/ui/web";
import { client } from "./api.js";
import { AuthScreen } from "./auth-screen.js";
import { ModuleSurface } from "./module-surface.js";
import { People } from "./people.js";
import { Poke } from "./poke.js";
import { Shell } from "./shell.js";
import { Rescue, SpaceGallery, SpaceOverview, SpaceSettings } from "./space-surfaces.js";
import { ReleasedApp } from "./released-app.js";
import { message } from "./shared.js";
import { SnowieSettings } from "./snowie-settings.js";
import { SnowieStatus } from "./snowie-status.js";

interface RouterContext { queryClient: QueryClient }

function Root() {
  const config = useQuery({ queryKey: ["config"], queryFn: client.config });
  const session = useQuery({ queryKey: ["session"], queryFn: client.session, retry: false });
  if (config.isLoading || session.isLoading) return <div className={ui("center-state")}><BrandMark /><LoadingState>Opening Snowiespace…</LoadingState></div>;
  if (config.error) return <div className={ui("center-state")}><StateNotice kind="offline" title="Snowiespace is unavailable">{message(config.error)}</StateNotice></div>;
  if (session.error) return <AuthScreen setupRequired={config.data?.setupRequired ?? false} />;
  return session.data ? <Shell session={session.data} /> : <AuthScreen setupRequired={config.data?.setupRequired ?? false} />;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Root });
const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: SpaceGallery });
const spaceRoute = createRoute({ getParentRoute: () => rootRoute, path: "/spaces/$spaceId", component: SpaceOverview });
const spaceSettingsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/spaces/$spaceId/settings", component: SpaceSettings });
const snowieSettingsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/spaces/$spaceId/settings/snowie", component: SnowieSettings });
const snowieStatusRoute = createRoute({ getParentRoute: () => rootRoute, path: "/spaces/$spaceId/snowie", component: SnowieStatus });
const pokeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/spaces/$spaceId/poke",
  validateSearch: (search: Record<string, unknown>) => ({
    targetKind: typeof search.targetKind === "string" ? search.targetKind : undefined,
    targetId: typeof search.targetId === "string" ? search.targetId : undefined,
    conversationId: typeof search.conversationId === "string" ? search.conversationId : undefined,
    jobId: typeof search.jobId === "string" ? search.jobId : undefined,
  }),
  component: Poke,
});
const moduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/spaces/$spaceId/modules/$moduleId",
  validateSearch: (search: Record<string, unknown>) => ({ appId: typeof search.appId === "string" ? search.appId : undefined }),
  component: ModuleSurface,
});
const releasedAppRoute = createRoute({ getParentRoute: () => rootRoute, path: "/spaces/$spaceId/apps/$appId", component: ReleasedApp });
const peopleRoute = createRoute({ getParentRoute: () => rootRoute, path: "/spaces/$spaceId/people", component: People });
const rescueRoute = createRoute({ getParentRoute: () => rootRoute, path: "/spaces/$spaceId/rescue", component: Rescue });
const routeTree = rootRoute.addChildren([indexRoute, spaceRoute, spaceSettingsRoute, snowieSettingsRoute, snowieStatusRoute, pokeRoute, moduleRoute, releasedAppRoute, peopleRoute, rescueRoute]);

export const router = createRouter({ routeTree, context: { queryClient: undefined! }, defaultPreload: "intent" });
declare module "@tanstack/react-router" { interface Register { router: typeof router } }
